import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layers } from 'lucide-react';
import { ExamGenApi, type ExamSetListRow } from '../../pages/Exams/generate/client';
import { PanelCard, ShowMoreFooter, TextLink } from '../../school_panel/components/ui';

const TILE = 'flex min-h-[76px] min-w-0 items-center gap-3.5 rounded-[18px] border border-line bg-surface p-4 text-ink no-underline transition hover:-translate-y-0.5 hover:text-ink hover:shadow-card-hover';

/** "28 Sep" (the artboard's "created 28 Sep"). */
function shortDate(iso: string) {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

/**
 * Exam-list "Exam sets" card (design `.qa.s4` tiles): the sets the viewer can see, newest first.
 * The detail page is owner-only, so a tile links only when `is_mine`. Any failure (an older
 * backend answers 404) hides the card quietly, without retries.
 */
export default function ExamSetsCard() {
    const [sets, setSets] = useState<ExamSetListRow[]>([]);
    const [showAll, setShowAll] = useState(false);

    useEffect(() => {
        let alive = true;
        ExamGenApi.examSets(20).then((rows) => { if (alive) setSets(rows); }).catch(() => { if (alive) setSets([]); });
        return () => { alive = false; };
    }, []);

    if (sets.length === 0) return null;
    return (
        <PanelCard
            aria-label="Exam sets"
            className="mt-[18px]"
            title="Exam sets"
            subtitle="Forms generated together from one blueprint"
            actions={<TextLink to="/exams/generate">Generate a set →</TextLink>}
        >
            <ul className="grid grid-cols-1 gap-[18px] md:grid-cols-2 xl:grid-cols-3">
                {(showAll ? sets : sets.slice(0, 6)).map((s) => {
                    const meta = [s.subject_name, s.created_at ? `created ${shortDate(s.created_at)}` : ''].filter(Boolean).join(' · ');
                    const body = (
                        <>
                            <span className="grid h-11 w-11 flex-none place-items-center rounded-[14px] bg-mist text-slate-ink"><Layers size={22} strokeWidth={1.8} aria-hidden="true" /></span>
                            <span className="min-w-0 flex-1">
                                <span className="block truncate font-semibold">{s.name}</span>
                                <span className="block truncate text-[13px] text-muted">{meta}</span>
                            </span>
                            <span className="font-mono font-semibold">{s.forms} {s.forms === 1 ? 'form' : 'forms'}</span>
                        </>
                    );
                    return (
                        <li key={s.id}>
                            {s.is_mine
                                ? <Link to={`/exams/exam-set/${s.id}`} className={TILE}>{body}</Link>
                                : <div className={TILE.replace(' hover:-translate-y-0.5 hover:text-ink hover:shadow-card-hover', '')}>{body}</div>}
                        </li>
                    );
                })}
            </ul>
            <ShowMoreFooter className="mt-3.5" shown={showAll ? sets.length : Math.min(6, sets.length)} total={sets.length} expanded={showAll} onToggle={() => setShowAll((v) => !v)} />
        </PanelCard>
    );
}
